<template>
   <result-wrapper :element-data="elementData">
      <template v-if="imageData && zoomSelection !== 'small-view'">
         <img ref="thumbnail" :src="imageData" @click="openDialog()" />
         <template v-for="(item, idx) in elementData.featuredresultspanel" :key="idx">
            <div class="bounding-box fr-overlay" :style="getBoundingBoxStyle(boxOf(item))">
               <highlight-result :element-data="item" :class="getMarkupClasses(item)" @click="openDialog()" />
            </div>
         </template>
         <div v-if="showDialog" class="dialog-container">
            <FeaturedResultsDialog :element-data="elementData" @hide="closeDialog()" />
         </div>
      </template>

      <div v-else class="markup-container">
         <div class="card-title ellipsis featured-results">Featured Results Panel</div>
      </div>
 

      <template #small-view>
         <small-view-result icon-name="featured-results" @click="openDialog()"/> 
         <div v-if="showDialog" class="dialog-container">
            <FeaturedResultsDialog :element-data="elementData" @hide="closeDialog()" />
         </div>
      </template>

   </result-wrapper>
</template>
  


<script>
import ResultWrapper from "../ResultWrapper.vue";
import FeaturedResultsDialog from "../FeaturedResultsDialog.vue";
import HighlightResult from "@/components/card/HighlightResult.vue";
import SmallViewResult from "@/components/card/SmallViewResult.vue";
import { mapGetters } from "vuex";

export default {
   name: "FeaturedResultsPanel",
   props: {
      elementKey: String,
      elementData: Object,
   },
   data() {
      return {
         showDialog: false,
         renderedWidth: 0,  // Default value
         renderedHeight: 0,  // Default value
         naturalWidth: 0,   // screenshot's own pixel size — see updateRenderedDimensions
         naturalHeight: 0,
      }
   },
   components: {
      ResultWrapper,
      FeaturedResultsDialog,
      HighlightResult,
      SmallViewResult
   },
   computed: {
      // The origin every card box is measured against. Prefer the panel's own css box;
      // when it is absent (observed live 2026-08-06: per-card css present, panel css
      // not) fall back to the minimum x/y of the card boxes — the cards tile the
      // panel, so their minimum IS the panel origin. Without this the subtraction was
      // 0 and every overlay sat one panel-offset too far right and down.
      panelBox() {
         const p = this.boxOf(this.elementData);
         if (p && p.width && p.height) return p;
         const boxes = (this.elementData?.featuredresultspanel || [])
            .map((i) => this.boxOf(i)).filter(Boolean);
         if (!boxes.length) return p || {};
         // Derive the WHOLE box from the cards, size included. Returning width 0 here
         // made getBoundingBoxStyle fall back to the image's own pixels — and the card
         // shows fr_thumbnail.png (~310px wide) while the coordinates are page pixels
         // (~1100), so every overlay came out ~3.5x too large and spilled out of the
         // strip. The cards tile the panel, so their extent IS the panel's extent.
         const x = Math.min(...boxes.map((b) => b.x));
         const y = Math.min(...boxes.map((b) => b.y));
         return {
            x, y,
            width: p?.width || (Math.max(...boxes.map((b) => b.x + b.width)) - x),
            height: p?.height || (Math.max(...boxes.map((b) => b.y + b.height)) - y),
         };
      },
      imageData() {
         const imageUrl = this.elementData?.featuredresultsthumbnail || null;
         if (imageUrl) {
            // Don't add cache buster to data URIs - it breaks them
            if (imageUrl.startsWith('data:')) {
               return imageUrl;
            }
            const cacheBuster = new Date().getTime(); // generate a unique cache buster
            return `${imageUrl}?t=${cacheBuster}`; // append the cache buster to the URL
         }
         return null;
      },
      ...mapGetters("toolbar", {
         zoomSelection: "zoomSelection",
      }),
   },
   methods: {
      updateRenderedDimensions() {
         if (this.$refs.thumbnail) {
               this.renderedWidth = this.$refs.thumbnail.offsetWidth;
               this.renderedHeight = this.$refs.thumbnail.offsetHeight;
               // The screenshot's own pixel size — the correct scale denominator. The
               // stored panel box is measured at a DIFFERENT moment than the screenshot
               // crop and can disagree (observed live: css height 336 vs image height
               // 312), which skewed every overlay ~7% vertically. The image cannot
               // disagree with itself.
               this.naturalWidth = this.$refs.thumbnail.naturalWidth;
               this.naturalHeight = this.$refs.thumbnail.naturalHeight;
            }
      },

      setInitialRenderedDimensions() {
         const img = this.$refs.thumbnail;
         if (!img) return;
         // A ResizeObserver instead of img.onload: assigning onload after mount MISSES a
         // cached or data-URI image that already finished loading, leaving the measured
         // size at 0 — and with size 0 the overlay boxes fall back to unscaled page
         // coordinates over a shrunken image, i.e. visibly misaligned. The observer fires
         // on first layout and again whenever the rendered size changes.
         if (this._thumbObserver) this._thumbObserver.disconnect();
         this._thumbObserver = new ResizeObserver(this.updateRenderedDimensions);
         this._thumbObserver.observe(img);
         this.updateRenderedDimensions();
      },
      openDialog() {
         this.showDialog = true;
      },
      closeDialog() {
         this.showDialog = false;
      },
      /**
       * The bounding box of a panel or one of its cards, as {x, y, width, height}.
       *
       * The parser writes snake_case — `css.bounding_box.{x,y,width,height}` — because it
       * comes straight from Playwright's `bounding_box()` (see browsers-web-service
       * serp/shared/featured_screenshot.py). This component previously read PascalCase
       * (`css.BoundingBox.{X,Y,Width,Height}`), which no poll has ever produced: verified
       * 2026-08-06 against browser-polled poll 115240560 and API-polled polls alike, both
       * store `bounding_box` with lowercase keys and neither has a `BoundingBox` key at
       * all. Every call therefore got `undefined` and no markup box was ever positioned.
       *
       * Both spellings are accepted here so the overlay cannot silently break again if
       * some other producer ever writes the PascalCase form.
       */
      boxOf(node) {
         const css = node?.css;
         if (!css) return null;
         const box = css.bounding_box || css.BoundingBox;
         if (!box) return null;
         return {
            x: box.x ?? box.X ?? 0,
            y: box.y ?? box.Y ?? 0,
            width: box.width ?? box.Width ?? 0,
            height: box.height ?? box.Height ?? 0,
         };
      },
      getBoundingBoxStyle(box) {
         if (box) {
            // The panel's own box, i.e. the origin the card boxes are measured against.
            const panel = this.panelBox;
            // Scale from the PANEL's coordinate space, not the image's own pixels.
            // The card shows fr_thumbnail.png (310x88) while the coordinates are in
            // panel pixels (~1100 wide) — using naturalWidth here put every box ~3.5x
            // too far right and too large, spilling out of the strip. (The dialog shows
            // the full-size fr.png, where the two happen to agree; it scales by naturals
            // for the separate reason that the stored panel box and the screenshot crop
            // can disagree — 336 vs 312 observed.)
            const originalWidth = panel.width || this.naturalWidth || 1;
            const originalHeight = panel.height || this.naturalHeight || 1;

            const x_adjustment = panel.x || 0;
            const y_adjustment = panel.y || 0;

            // Calculate scale factors using stored thumbnail dimensions
            const xScale = this.renderedWidth / originalWidth;
            const yScale = this.renderedHeight / originalHeight;

            // Adjust bounding box coordinates
            const adjustedX = (box.x - x_adjustment) * xScale;
            const adjustedY = (box.y - y_adjustment) * yScale;
            const adjustedWidth = box.width * xScale;
            const adjustedHeight = box.height * yScale;

            return {
               top: `${adjustedY}px`,
               left: `${adjustedX}px`,
               width: `${adjustedWidth}px`,
               height: `${adjustedHeight}px`,
               position: "absolute",
               cursor: "pointer",  // Change the cursor to indicate it's clickable
            };
         }
      }
   },
   watch: {
      // Assuming that `elementData` is the reactive data that `getMarkupClasses` depends on
      elementData: {
         deep: true,
         handler() {
            this.$forceUpdate();
         }
      },
   },
   mounted() {
      this.$nextTick(() => {
         this.setInitialRenderedDimensions();
      });
   },
   beforeUnmount() {
      if (this._thumbObserver) this._thumbObserver.disconnect();
   },
   updated() {
      this.$nextTick(() => {
      //   this.setInitialRenderedDimensions();
         this.updateRenderedDimensions();
      });
   },

};
</script>

<style lang="scss" scoped>
.image-container {
   position: relative;
}

.bounding-box {
   position: absolute;
   z-index: 10;
}

img {
   border-radius: var(--bs-border-radius);
   max-width: 82%;
   cursor: pointer;
   position: relative;
   z-index: 5;
}

.featured-results {
   font-size: 13px;
   font-weight: bold;
}

.card-with-markup-container {
   z-index: 100 !important;

   .image-container {
      position: relative;
      display: inline-block;
   }

   .bounding-box {
      .highlight-element {
         height: 100%;
         opacity: .5;
         cursor: pointer;
         // NO `border: 0` here. Scoped styles compile with a `[data-v-…]` attribute, so
         // this rule is `.card-with-markup-container .bounding-box .highlight-element[data-v-x]`
         // at specificity 0,4,0 — above BOTH `.fr-overlay .<value>` (0,3,0, the markup
         // colour) and `.fr-overlay :where(.highlight-element)` (0,2,0, the transparent
         // base) in _markup.scss. `border: 0` therefore silently erased the marked
         // overlay's outline on the CARD while the dialog — whose style block is not
         // scoped — showed it correctly. Nothing else paints a border on
         // `.highlight-element`, so no reset is needed.
         // Guarded by tests/featuredResultsMarkup.test.js.

         .markup-link-container {
            height: 100%;

            &.markup-link-text {
               color: transparent;
            }
         }
      }
   }
}
</style>